import { AuditReportsPublicJobDto } from '@mi-front/api-v2';
import { JobProgress } from '@app/components/base/JobProgress';
import { LDate } from '@app/components/date/LDate';
import { LTime } from '@app/components/date/LTime';
import { ReportTypeChip } from '@app/components/ReportTypeChip';
import { StationLabel } from '@app/modules/admin/screens/stations/list/StationLabel';
import { useStationsWithAudit } from '@app/modules/audit/hooks/useStationsWithAudit';
import { KeyValue } from '@app/core/components/KeyValue';
import { AuditReportsReportRun } from '@mi-front/api-v2';
import { LocaleRelativeTime } from '@mi-front/ui';
import { Box, Grid, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { getBroadcastEvidenceConfig } from './broadcast-evidence-config';
import { BroadcastEvidenceSummaryKpis } from './BroadcastEvidenceSummaryKpis';

interface Props {
  report: AuditReportsReportRun;
}

/**
 * Header info for a broadcast evidence report run: identity, job, and config summary.
 */
export function BroadcastEvidenceReportInfo({ report }: Props) {
  const config = getBroadcastEvidenceConfig(report);
  const stations = useStationsWithAudit();
  const station = stations.find((s) => s.id === config.stationId);

  return (
    <Box>
      <KeyValue
        label={<FormattedMessage id="audit.reportsView.name" defaultMessage="Nombre del reporte" />}
      >
        {report.name}
      </KeyValue>

      <KeyValue label={<FormattedMessage id="audit.reportsView.type" defaultMessage="Tipo" />}>
        <ReportTypeChip reportType={report.type} />
      </KeyValue>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <KeyValue
            label={
              <FormattedMessage id="audit.reportsView.created" defaultMessage="Fecha de creación" />
            }
          >
            <Box sx={{ display: 'inline-flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <LDate
                  value={report.createdAt}
                  day="2-digit"
                  month="2-digit"
                  year="2-digit"
                  weekday="short"
                  className="text-capitalize"
                />
                <LTime value={report.createdAt} />
              </Box>
              <Typography color="text.disabled" variant="caption">
                <LocaleRelativeTime value={report.createdAt} />
              </Typography>
            </Box>
          </KeyValue>
        </Grid>
        {report.job && (
          <Grid item xs={12} md={4}>
            <KeyValue
              label={
                <FormattedMessage id="audit.broadcastEvidence.jobStatus" defaultMessage="Estado" />
              }
            >
              <JobProgress job={report.job as AuditReportsPublicJobDto} size="small" />
            </KeyValue>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <KeyValue
            label={
              <FormattedMessage id="audit.broadcastEvidence.station" defaultMessage="Emisora" />
            }
          >
            {station ? <StationLabel station={station} /> : config.stationId}
          </KeyValue>
        </Grid>
        <Grid item xs={12} md={4}>
          <KeyValue
            label={
              <FormattedMessage id="audit.broadcastEvidence.localDate" defaultMessage="Fecha" />
            }
          >
            <LDate
              value={`${config.localDate}T12:00:00`}
              day="2-digit"
              month="2-digit"
              year="2-digit"
              weekday="short"
              className="text-capitalize"
              tz={config.timezone}
            />
          </KeyValue>
        </Grid>
        <Grid item xs={12} md={4}>
          <KeyValue
            label={<FormattedMessage id="audit.broadcastEvidence.orderId" defaultMessage="Orden" />}
          >
            {config.orderId}
          </KeyValue>
        </Grid>
        <Grid item xs={12} md={4}>
          <KeyValue
            label={
              <FormattedMessage
                id="audit.broadcastEvidence.scheduledMaterial"
                defaultMessage="Material pautado"
              />
            }
          >
            {config.scheduledMaterial.customId}
            {config.scheduledMaterial.version ? ` (${config.scheduledMaterial.version})` : ''}
          </KeyValue>
        </Grid>
      </Grid>

      <BroadcastEvidenceSummaryKpis summary={config.summary} />
    </Box>
  );
}
